import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, X, Save, Workflow, Flag, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { workflowsApi } from '../../services/workflowsApi';
import { rbacApi } from '../../services/rbacApi';
import StateNode from './StateNode';

const nodeTypes = { state: StateNode };
const COLOR_OPTIONS = ['', 'green', 'amber', 'red', 'blue', 'orange', 'gray'];
const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

// Build React Flow nodes/edges from a stored workflow definition.
const rowToGraph = (row) => {
  const idByCode = {};
  const nodes = (row.states || []).map((s, i) => {
    const id = uid();
    idByCode[s.code] = id;
    return {
      id,
      type: 'state',
      position: {
        x: s.pos_x != null ? s.pos_x : 80 + (i % 4) * 220,
        y: s.pos_y != null ? s.pos_y : 80 + Math.floor(i / 4) * 140,
      },
      data: {
        code: s.code,
        label: s.label,
        is_initial: !!s.is_initial,
        is_terminal: !!s.is_terminal,
        color: s.color || '',
      },
    };
  });
  const edges = (row.transitions || [])
    .filter((t) => idByCode[t.from_state] && idByCode[t.to_state])
    .map((t) => ({
      id: uid(),
      source: idByCode[t.from_state],
      target: idByCode[t.to_state],
      label: t.action_label,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: {
        action_label: t.action_label,
        allowed_roles: t.allowed_roles || [],
        requires_comment: !!t.requires_comment,
      },
    }));
  return { nodes, edges };
};

const WorkflowCanvasDesigner = ({ open, onClose, editRow, doctypes, onSaved }) => {
  const [doctype, setDoctype] = useState('');
  const [name, setName] = useState('');
  const [stateField, setStateField] = useState('status');
  const [active, setActive] = useState(true);
  const [roles, setRoles] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selNode, setSelNode] = useState(null);
  const [selEdge, setSelEdge] = useState(null);

  useEffect(() => {
    rbacApi.listRoles().then((r) => setRoles((r.data || []).map((x) => x.name))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelNode(null);
    setSelEdge(null);
    if (editRow) {
      setDoctype(editRow.doctype);
      setName(editRow.name);
      setStateField(editRow.state_field || 'status');
      setActive(!!editRow.active);
      const g = rowToGraph(editRow);
      setNodes(g.nodes);
      setEdges(g.edges);
    } else {
      setDoctype(doctypes[0] || '');
      setName('');
      setStateField('status');
      setActive(true);
      const id = uid();
      setNodes([{
        id,
        type: 'state',
        position: { x: 120, y: 140 },
        data: { code: 'DRAFT', label: 'Draft', is_initial: true, is_terminal: false, color: 'gray' },
      }]);
      setEdges([]);
    }
  }, [open, editRow, doctypes, setNodes, setEdges]);

  const onConnect = useCallback((conn) => {
    if (conn.source === conn.target) {
      toast.error('A transition cannot loop back to the same state');
      return;
    }
    setEdges((eds) => {
      if (eds.some((e) => e.source === conn.source && e.target === conn.target)) {
        toast.error('A transition between these states already exists');
        return eds;
      }
      return addEdge({
        ...conn,
        id: uid(),
        label: 'Action',
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { action_label: 'Action', allowed_roles: [], requires_comment: false },
      }, eds);
    });
  }, [setEdges]);

  const onNodeClick = useCallback((_, node) => { setSelNode(node.id); setSelEdge(null); }, []);
  const onEdgeClick = useCallback((_, edge) => { setSelEdge(edge.id); setSelNode(null); }, []);
  const onPaneClick = useCallback(() => { setSelNode(null); setSelEdge(null); }, []);

  const addStateNode = () => {
    const id = uid();
    setNodes((nds) => [...nds, {
      id,
      type: 'state',
      position: { x: 120 + (nds.length % 4) * 220, y: 140 + Math.floor(nds.length / 4) * 140 },
      data: { code: '', label: '', is_initial: false, is_terminal: false, color: '' },
    }]);
    setSelNode(id);
    setSelEdge(null);
  };

  const patchNode = (id, patch) => {
    setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const patchEdge = (id, patch) => {
    setEdges((eds) => eds.map((e) => {
      if (e.id !== id) return e;
      const data = { ...e.data, ...patch };
      return { ...e, data, label: data.action_label };
    }));
  };

  const deleteNode = (id) => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    setSelNode(null);
  };

  const deleteEdge = (id) => {
    setEdges((eds) => eds.filter((e) => e.id !== id));
    setSelEdge(null);
  };

  const selectedNodeObj = useMemo(() => nodes.find((n) => n.id === selNode), [nodes, selNode]);
  const selectedEdgeObj = useMemo(() => edges.find((e) => e.id === selEdge), [edges, selEdge]);

  const handleSave = async () => {
    if (!doctype || !name.trim()) {
      toast.error('DocType and Name are required');
      return;
    }
    const codeById = {};
    const states = nodes.map((n) => {
      codeById[n.id] = (n.data.code || '').toUpperCase().trim();
      return {
        code: (n.data.code || '').toUpperCase().trim(),
        label: (n.data.label || '').trim(),
        is_initial: !!n.data.is_initial,
        is_terminal: !!n.data.is_terminal,
        color: n.data.color || null,
        pos_x: Math.round(n.position.x),
        pos_y: Math.round(n.position.y),
      };
    });
    if (states.length < 2) { toast.error('At least 2 states are required'); return; }
    if (states.some((s) => !s.code || !s.label)) {
      toast.error('Every state needs a CODE and a Label'); return;
    }
    const codes = states.map((s) => s.code);
    if (new Set(codes).size !== codes.length) { toast.error('Duplicate state codes are not allowed'); return; }
    const initials = states.filter((s) => s.is_initial);
    if (initials.length !== 1) { toast.error('Exactly one state must be marked Initial'); return; }

    const transitions = edges.map((e) => ({
      from_state: codeById[e.source],
      to_state: codeById[e.target],
      action_label: (e.data?.action_label || '').trim(),
      allowed_roles: e.data?.allowed_roles || [],
      requires_comment: !!e.data?.requires_comment,
    }));
    if (transitions.some((t) => !t.action_label)) {
      toast.error('Every transition needs an action label (click the arrow to edit)'); return;
    }

    const payload = { doctype, name: name.trim(), state_field: stateField.trim() || 'status', active, states, transitions };
    try {
      if (editRow) {
        await workflowsApi.update(editRow.workflow_id, payload);
        toast.success('Workflow updated');
      } else {
        await workflowsApi.create(payload);
        toast.success('Workflow created');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="wf-canvas-designer"
      >
        <DialogTitle className="sr-only">{editRow ? 'Edit' : 'New'} Workflow Designer</DialogTitle>
        <DialogDescription className="sr-only">
          Visually design states and role-based transitions on a drag-and-drop canvas.
        </DialogDescription>
        {/* Header bar */}
        <div className="flex items-center gap-3 border-b px-5 py-3 bg-stone-50 flex-wrap">
          <div className="flex items-center gap-2 mr-2">
            <Workflow className="w-5 h-5 text-orange-600" />
            <span className="font-semibold text-stone-800">{editRow ? 'Edit' : 'New'} Workflow</span>
          </div>
          <div className="w-40">
            <Select value={doctype} onValueChange={setDoctype} disabled={!!editRow}>
              <SelectTrigger data-testid="wf-canvas-doctype" className="h-9"><SelectValue placeholder="DocType *" /></SelectTrigger>
              <SelectContent>
                {doctypes.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Input
            className="h-9 w-56"
            placeholder="Workflow name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="wf-canvas-name"
          />
          <Input
            className="h-9 w-40"
            placeholder="state field"
            value={stateField}
            onChange={(e) => setStateField(e.target.value)}
            data-testid="wf-canvas-state-field"
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={active} onCheckedChange={(v) => setActive(!!v)} data-testid="wf-canvas-active" />
            Active
          </label>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={addStateNode} data-testid="wf-canvas-add-state">
              <Plus className="w-4 h-4 mr-1" />Add State
            </Button>
            <Button size="sm" onClick={handleSave} data-testid="wf-canvas-save" className="bg-stone-800 hover:bg-stone-700">
              <Save className="w-4 h-4 mr-1" />Save
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="wf-canvas-close">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body: canvas + side panel */}
        <div className="flex flex-1 min-h-0">
          <div className="flex-1 min-w-0" data-testid="wf-canvas-flow">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={16} color="#e7e5e4" />
              <Controls />
              <MiniMap pannable zoomable className="!bg-white" />
            </ReactFlow>
          </div>

          {/* Side panel */}
          <div className="w-80 border-l bg-white overflow-y-auto p-4 shrink-0" data-testid="wf-canvas-panel">
            {!selectedNodeObj && !selectedEdgeObj && (
              <div className="text-sm text-stone-500 space-y-3">
                <p className="font-medium text-stone-700">How to use</p>
                <ul className="list-disc pl-4 space-y-1.5">
                  <li>Click <strong>Add State</strong> to drop a state, then drag to position it.</li>
                  <li>Drag from a state's <span className="text-orange-600 font-medium">right dot</span> to another state's left dot to create a transition.</li>
                  <li>Click a state or a transition arrow to edit its properties here.</li>
                  <li>Mark exactly one state as <strong>Initial</strong>.</li>
                </ul>
              </div>
            )}

            {selectedNodeObj && (
              <div className="space-y-3" data-testid="wf-node-panel">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-800">State</h3>
                  <Button variant="ghost" size="icon" onClick={() => deleteNode(selectedNodeObj.id)} data-testid="wf-node-delete">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <div>
                  <Label className="text-xs">Code *</Label>
                  <Input
                    className="font-mono uppercase"
                    value={selectedNodeObj.data.code}
                    placeholder="UPPER_SNAKE_CASE"
                    onChange={(e) => patchNode(selectedNodeObj.id, { code: e.target.value.toUpperCase() })}
                    data-testid="wf-node-code"
                  />
                </div>
                <div>
                  <Label className="text-xs">Label *</Label>
                  <Input
                    value={selectedNodeObj.data.label}
                    onChange={(e) => patchNode(selectedNodeObj.id, { label: e.target.value })}
                    data-testid="wf-node-label"
                  />
                </div>
                <div>
                  <Label className="text-xs">Colour</Label>
                  <Select
                    value={selectedNodeObj.data.color || 'none'}
                    onValueChange={(v) => patchNode(selectedNodeObj.id, { color: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="wf-node-color"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default</SelectItem>
                      {COLOR_OPTIONS.filter(Boolean).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={selectedNodeObj.data.is_initial}
                    onCheckedChange={(v) => patchNode(selectedNodeObj.id, { is_initial: !!v })}
                    data-testid="wf-node-initial"
                  />
                  <Flag className="w-3.5 h-3.5 text-green-600" /> Initial state
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={selectedNodeObj.data.is_terminal}
                    onCheckedChange={(v) => patchNode(selectedNodeObj.id, { is_terminal: !!v })}
                    data-testid="wf-node-terminal"
                  />
                  <CheckCircle2 className="w-3.5 h-3.5 text-stone-600" /> Terminal state
                </label>
              </div>
            )}

            {selectedEdgeObj && (
              <div className="space-y-3" data-testid="wf-edge-panel">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-800">Transition</h3>
                  <Button variant="ghost" size="icon" onClick={() => deleteEdge(selectedEdgeObj.id)} data-testid="wf-edge-delete">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <p className="text-xs text-stone-500 font-mono">
                  {nodes.find((n) => n.id === selectedEdgeObj.source)?.data.code || '?'}
                  {' → '}
                  {nodes.find((n) => n.id === selectedEdgeObj.target)?.data.code || '?'}
                </p>
                <div>
                  <Label className="text-xs">Action label *</Label>
                  <Input
                    value={selectedEdgeObj.data?.action_label || ''}
                    placeholder="e.g. Submit for Approval"
                    onChange={(e) => patchEdge(selectedEdgeObj.id, { action_label: e.target.value })}
                    data-testid="wf-edge-action"
                  />
                </div>
                <div>
                  <Label className="text-xs">Allowed roles</Label>
                  <p className="text-[11px] text-stone-400 mb-1">No selection = any authenticated user</p>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto border rounded p-2">
                    {roles.length === 0 && <span className="text-xs text-stone-400">No roles found</span>}
                    {roles.map((role) => {
                      const checked = (selectedEdgeObj.data?.allowed_roles || []).includes(role);
                      return (
                        <label key={role} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const cur = selectedEdgeObj.data?.allowed_roles || [];
                              const next = v ? [...cur, role] : cur.filter((r) => r !== role);
                              patchEdge(selectedEdgeObj.id, { allowed_roles: next });
                            }}
                            data-testid={`wf-edge-role-${role}`}
                          />
                          {role}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!selectedEdgeObj.data?.requires_comment}
                    onCheckedChange={(v) => patchEdge(selectedEdgeObj.id, { requires_comment: !!v })}
                    data-testid="wf-edge-comment"
                  />
                  Require a comment
                </label>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowCanvasDesigner;
